// ---------------------------------------------------------------------------
// SVG chart helpers — pure math + the single sanitization point for chart SVG
// ---------------------------------------------------------------------------
//
// Chart functions build SVG STRINGS. Every dynamic token (label text, ids,
// numeric coordinates already come from our own math) routed into that string
// MUST pass through `svgText` / `svgAttr` here so there is ONE owner of the
// SVG-injection invariant. Reuses the canonical escapeHtml.

import { escapeHtml } from '../dom/escape-html.js';

/**
 * Escape a value for use as SVG/HTML text content or an attribute value.
 * (XML attribute and text escaping needs the same five entities escapeHtml
 * covers, so we reuse it rather than fork a parallel escaper.)
 *
 * @param {unknown} value
 * @returns {string}
 */
export function svgText(value) {
  return escapeHtml(value == null ? '' : String(value));
}

/** Alias for readability at attribute call sites. @param {unknown} value */
export function svgAttr(value) {
  return svgText(value);
}

/**
 * Map a value from a data domain onto a pixel range. Returns `rangeMin` when the
 * domain is degenerate (min === max) to avoid divide-by-zero.
 *
 * @param {number} value
 * @param {number} domainMin
 * @param {number} domainMax
 * @param {number} rangeMin
 * @param {number} rangeMax
 * @returns {number}
 */
export function scaleLinear(value, domainMin, domainMax, rangeMin, rangeMax) {
  if (domainMax === domainMin) return rangeMin;
  const t = (value - domainMin) / (domainMax - domainMin);
  return rangeMin + t * (rangeMax - rangeMin);
}

/**
 * Convert a polar coordinate (centre + radius + angle) to cartesian. Angle is in
 * degrees, measured clockwise from 12 o'clock (so 0° is straight up) — the
 * convention donut slices are laid out with.
 *
 * @param {number} cx
 * @param {number} cy
 * @param {number} radius
 * @param {number} angleDeg
 * @returns {{ x: number, y: number }}
 */
export function polarToCartesian(cx, cy, radius, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
}

/**
 * Format a number for compact display in chart labels (thousands grouping, no
 * trailing decimals for integers). Locale-free and deterministic for tests.
 *
 * @param {number} value
 * @returns {string}
 */
export function numberFormat(value) {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 100) / 100;
  const [intPart, frac] = String(rounded).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac ? `${grouped}.${frac}` : grouped;
}

/**
 * Round a coordinate to 2 decimals so generated SVG strings are stable and
 * compact (and unit assertions are exact).
 *
 * @param {number} n
 * @returns {number}
 */
export function round2(n) {
  return Math.round(n * 100) / 100;
}
